import React, { Component, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import style from './UploadModal.module.css';
import classnames from 'classnames';
import { selectImage } from '../../utils/file';

interface PortalProps {
  top?: string;
  root?: React.RefObject<HTMLDivElement> | null;
  onClose?(): void;
}
class Portal extends Component<PortalProps, any> {
  div: HTMLDivElement;
  container: HTMLDivElement;
  root: HTMLElement;
  constructor(props: PortalProps) {
    super(props);
    this.div = document.createElement('div');
    this.div.style.top = 'top' in props ? props.top! : '50%';
    this.div.style.left = '50%';
    this.div.style.position = 'absolute';
    this.div.style.transform = `translate(-50%, ${'top' in props ? 0 : '-50%'})`;

    this.container = document.createElement('div');
    this.container.style.position = 'absolute';
    this.container.style.width = '100%';
    this.container.style.height = '100%';
    this.container.style.left = '0';
    this.container.style.top = '0';
    this.container.style.zIndex = '20';
    this.container.appendChild(this.div);
    this.container.addEventListener('click', this.onClick);
    this.root = this.props.root?.current || document.body;
  }
  componentDidMount() {
    this.root.appendChild(this.container);
    window.addEventListener('keydown', this.onEsc);
  }
  componentWillUnmount() {
    this.container.removeEventListener('click', this.onClick);
    this.root.removeChild(this.container);
    window.removeEventListener('keydown', this.onEsc);
  }

  onEsc = (e: KeyboardEvent) => {
    // esc
    if (e.keyCode === 27) {
      const { onClose } = this.props;
      onClose && onClose();
    }
  };

  onClick = (e: MouseEvent) => {
    const { onClose } = this.props;
    if (e.target === this.container && onClose) onClose();
  };

  render() {
    const { children } = this.props;
    return createPortal(children, this.div);
  }
}

let filesCache: File[] = [];
export function UploadModal({
  children,
  onSave,
  files,
  title,
  container
}: {
  children: React.ReactElement;
  files: string[];
  onSave(urls: string[], files: File[]): Promise<any>;
  onCancel?(): void;
  title: string;
  container?: React.RefObject<HTMLDivElement>;
}) {
  const [visiable, setVisiable] = useState(false);
  const [_files, setFiles] = useState(files);
  const [uploading, setUploading] = useState(false);

  const onClick = useCallback(
    (index: number) => {
      selectImage((file, path) => {
        filesCache = [file];
        setFiles([path]);
      });
    },
    [_files]
  );

  const _onClose = () => setVisiable(false);
  const _onSave = () => {
    if (!filesCache.length) return _onClose();
    setUploading(true);
    onSave(_files, filesCache)
      .then(_onClose)
      .catch(() => {})
      .then(() => setUploading(false));
  };
  return (
    <React.Fragment>
      {React.cloneElement(children, { onClick: () => (setVisiable(true), (filesCache = [])) })}
      {visiable && (
        <Portal onClose={_onClose} root={container}>
          <div className={style.container}>
            <span className={style.line_top}></span>
            <span className={style.line_bottom}></span>
            <div className={style.title}>{title}</div>
            <a className={classnames('iconfont icon-btn_close', style.close)} onClick={_onClose}></a>
            <div className={style.main}>
              {_files.map((image, index) => (
                <div key={index} className={style.img}>
                  <span className={classnames(style.img__border, style['img__border--topleft'])}></span>
                  <span className={classnames(style.img__border, style['img__border--topright'])}></span>
                  <span className={classnames(style.img__border, style['img__border--bottomleft'])}></span>
                  <span className={classnames(style.img__border, style['img__border--bottomright'])}></span>
                  <img key={index} src={image} onClick={(e) => onClick(index)} />
                </div>
              ))}
            </div>
            <div className={style.footer}>
              <a
                className={classnames(style.button, style['button--primary'], {
                  [style['button--disabled']]: uploading
                })}
                onClick={() => {
                  if (uploading) return;
                  _onSave();
                }}
              >
                保存 {uploading && <a className={classnames('iconfont icon-loading')}></a>}
              </a>
              <a className={style.button} onClick={_onClose}>
                取消
              </a>
            </div>
          </div>
        </Portal>
      )}
    </React.Fragment>
  );
}
